import React from "react";
import type { TimeSelectProps } from "@cmp-src/form/TimeSelect/types";
import { getNowDate } from "@gettactic/helpers/src/date-time/getNowDate";
import { format } from "date-fns";
import { Button } from "@cmp-src/primitives/button";
import { LiveIcon } from "@cmp-src/icons/icons";
import { PulsingNode } from "@cmp-src/components/PulsingNode/PulsingNode";
import { cn, type StartEndTimeMode, useDisclosure } from "@gettactic/helpers";
import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { TimelineRangePicker } from "@cmp-src/date-time/TimelineRangePicker";

export interface StartEndTimePickerProps {
  startDate?: Date | null;
  endDate?: Date | null;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  /** Called when both times change together (e.g., preset selection). Avoids race conditions. */
  onRangeChange?: (start: Date, end: Date) => void;
  /** Not used. */
  startTimeSelectProps?: Partial<TimeSelectProps>;
  /** Not used. */
  endTimeSelectProps?: Partial<TimeSelectProps>;
  /** `"live"` follows the current time; picking a time switches back to `"manual"`. */
  mode?: StartEndTimeMode;
  /** Shows the Live option. */
  allowLive?: boolean;
  onModeChange?: (mode: StartEndTimeMode) => void;
  className?: string;
  timeTriggerClassName?: string;
  /** Office timezone (IANA format, e.g., "America/New_York") for timezone-aware NOW line */
  timeZone?: string;
  /** Office opening hour (e.g., 9 for 9 AM) - used for "Full Day" preset */
  officeStartHour?: number;
  /** Office closing hour (e.g., 17 for 5 PM) - used for "Full Day" preset */
  officeEndHour?: number;
  /** Default meeting duration in minutes (used for "Meeting Now" preset) */
  defaultMeetingDuration?: number;
  /** Enable scrollable timeline with larger time scale (default: false) */
  scrollable?: boolean;
  /** Height multiplier for scrollable mode (default: 2) */
  scrollableHeightMultiplier?: number;
}

const radius = "rounded-v2 md:rounded-v2";
const timeFormat = "hh:mm a";
const classes = {
  Wrapper: `relative w-full flex bg-neutral-100 ${radius} h-[42px] md:mt-1`,
  AnimatedBackground: `absolute top-0 bottom-0 w-10 ${radius} bg-white transition-all border-1 border-neutral-200`,
  TimeTrigger:
    "w-full bg-transparent border-0 hover:bg-transparent text-[12px] md:text-[14px] cursor-pointer",
  TimePickersWrapper: "w-full flex relative items-center justify-center",
  TimeSelectWrapper: "p-0 m-0",
  LiveButton:
    "h-[42px] md:min-w-[78px] font-[500] text-[12px] md:text-[14px] text-content hover:bg-transparent"
};

/**
 * The time range of search and filter bars ("09:00 AM - 10:30 AM"): opens a
 * `TimelineRangePicker`. With `allowLive`, a Live option follows the current time
 * (`mode="live"`).
 */
export const StartEndTimePicker: React.FC<StartEndTimePickerProps> = (
  props
) => {
  const {
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    onRangeChange,
    mode = "manual",
    allowLive = false,
    onModeChange,
    className,
    timeTriggerClassName,
    timeZone,
    officeStartHour,
    officeEndHour,
    defaultMeetingDuration,
    scrollable,
    scrollableHeightMultiplier
  } = props;

  const popoverDisclosure = useDisclosure();

  const handleStartTimeChange = (time: Date) => {
    onStartDateChange(time);
    onModeChange?.("manual");
  };

  const handleEndTimeChange = (time: Date) => {
    onEndDateChange(time);
    onModeChange?.("manual");
  };

  const handleRangeChange = (start: Date, end: Date) => {
    // Use combined callback if available to avoid closure/race condition issues
    if (onRangeChange) {
      onRangeChange(start, end);
    } else {
      onStartDateChange(start);
      onEndDateChange(end);
    }
    onModeChange?.("manual");
  };

  const startTimeValue = React.useMemo(() => {
    return startDate ? format(startDate, timeFormat) : "";
  }, [startDate]);

  const endTimeValue = React.useMemo(() => {
    return endDate ? format(endDate, timeFormat) : "";
  }, [endDate]);

  const [wrapperRef, setWrapperRef] = React.useState<HTMLDivElement | null>(
    null
  );

  const [timeSelectorsWrapperRef, setTimeSelectorsWrapperRef] =
    React.useState<HTMLDivElement | null>(null);

  const [liveSelectorWrapperRef, setLiveSelectorWrapperRef] =
    React.useState<HTMLDivElement | null>(null);

  const [backgroundRef, setBackgroundRef] =
    React.useState<HTMLDivElement | null>(null);

  const [wrapperWidth, setWrapperWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    if (!wrapperRef) return;
    const updateWidth = () => {
      setWrapperWidth(wrapperRef.getBoundingClientRect().width);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [wrapperRef]);

  React.useLayoutEffect(() => {
    if (!backgroundRef) return;

    const updateBackgroundPosition = () => {
      if (!backgroundRef) return;

      let width = 0;
      let offsetLeft = 0;

      if (mode === "live" && liveSelectorWrapperRef) {
        width = liveSelectorWrapperRef.getBoundingClientRect()?.width || 0;
        offsetLeft = liveSelectorWrapperRef.offsetLeft;
      } else if (timeSelectorsWrapperRef) {
        width = timeSelectorsWrapperRef.getBoundingClientRect()?.width || 0;
        offsetLeft = timeSelectorsWrapperRef.offsetLeft;
      }

      backgroundRef.style.width = `${width}px`;
      backgroundRef.style.left = `${offsetLeft}px`;
    };

    // Initial measurement
    updateBackgroundPosition();

    // In some contexts (e.g. animated dialogs), the parent container is still
    // animating its scale/position when we first measure. Re-measure shortly
    // after mount to capture the final size.
    const timeoutId =
      typeof window !== "undefined"
        ? window.setTimeout(updateBackgroundPosition, 210)
        : undefined;

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    timeSelectorsWrapperRef,
    backgroundRef,
    mode,
    liveSelectorWrapperRef,
    startTimeValue,
    endTimeValue
  ]);

  const isLive = mode === "live";

  const timelineContent = (
    <TimelineRangePicker
      startTime={startDate || null}
      endTime={endDate || null}
      onStartTimeChange={handleStartTimeChange}
      onEndTimeChange={handleEndTimeChange}
      onRangeChange={handleRangeChange}
      date={startDate || getNowDate()}
      height={450}
      timeZone={timeZone}
      officeStartHour={officeStartHour}
      officeEndHour={officeEndHour}
      defaultMeetingDuration={defaultMeetingDuration}
      scrollable={scrollable}
      scrollableHeightMultiplier={scrollableHeightMultiplier}
    />
  );

  return (
    <div className={cn(classes.Wrapper, className)} ref={setWrapperRef}>
      <div className={classes.AnimatedBackground} ref={setBackgroundRef} />
      <div
        className={classes.TimePickersWrapper}
        ref={setTimeSelectorsWrapperRef}
      >
        <Popover
          className="p-0 max-md:w-screen max-md:max-w-full max-h-[calc(100vh-120px)] overflow-auto"
          bodyProps={{ className: "p-2" }}
          content={timelineContent}
          open={popoverDisclosure.isOpen}
          onOpenChange={popoverDisclosure.onOpenChange}
          contentProps={{
            style: { width: Math.max(wrapperWidth || 0, 380) },
            sideOffset: 8,
            align: "start",
            className:
              "max-md:!w-screen max-md:!left-0 max-md:!right-0 max-md:!transform-none",
            "aria-label": "Choose a time range"
          }}
          triggerAsChild
        >
          <button
            type="button"
            className={cn(
              classes.TimeTrigger,
              "flex items-center justify-center gap-0.5 px-3 py-2 whitespace-nowrap"
            )}
            onClick={() => {
              if (!isLive) {
                popoverDisclosure.open();
              }
            }}
          >
            <span className={cn(timeTriggerClassName)}>
              {startTimeValue || "--:--"}
            </span>
            <span className={cn(timeTriggerClassName, "text-content mx-0.5")}>
              -
            </span>
            <span className={cn(timeTriggerClassName)}>
              {endTimeValue || "--:--"}
            </span>
          </button>
        </Popover>
        {isLive && (
          <button
            type="button"
            aria-label="Choose a time"
            onClick={() => onModeChange?.("manual")}
            className={"absolute inset-0"}
          />
        )}
      </div>
      {allowLive && (
        <div className={"relative"} ref={setLiveSelectorWrapperRef}>
          <Button
            variant={"ghost"}
            className={cn(
              classes.LiveButton,
              isLive && "text-primary-1000 hover:text-primary-1000"
            )}
            onClick={() => {
              onModeChange?.("live");
            }}
            // The label is hidden on small screens
            aria-label="Live"
            aria-pressed={isLive}
          >
            <PulsingNode isDisabled={!isLive}>
              <LiveIcon className={"size-3.5"} />
            </PulsingNode>
            <span className={"hidden md:inline !font-inter"}>Live</span>
          </Button>
        </div>
      )}
    </div>
  );
};
